import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";

import { isPrerendering } from "@/lib/prerender";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/videos")({
  component: VideosPage,
});

type VideoItem = {
  id: string;
  title: string;
  published: string;
  thumbnail: string;
  description: string;
};

const PLAYLIST_ID = import.meta.env.VITE_YOUTUBE_VIDEOS_PLAYLIST_ID;
const CHANNEL_ID = import.meta.env.VITE_YOUTUBE_CHANNEL_ID;
const API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;
const CHANNEL_URL = "https://www.youtube.com/@larasoftsoftwareglobal";
const PER_PAGE = 6;

function useYouTubeVideos() {
  const [items, setItems] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Never fetch while prerendering. This page is in PRERENDER_ROUTES and CI
    // supplies the YouTube API key, so the build would call the live API - and
    // whatever came back, a video list or "YouTube API error: 403", would be
    // frozen into static HTML that Azure serves and caches to every visitor
    // until React boots. Same defect as /status; the fetch stays on the
    // client, where it belongs.
    if (isPrerendering()) return;

    if (!API_KEY) {
      setError("YouTube API key not configured");
      setLoading(false);
      return;
    }

    let mounted = true;

    async function fetchVideos() {
      try {
        const params = new URLSearchParams({
          part: "snippet",
          maxResults: "24",
          key: API_KEY,
        });

        if (PLAYLIST_ID) {
          params.set("playlistId", PLAYLIST_ID);
        } else if (CHANNEL_ID) {
          params.set("channelId", CHANNEL_ID);
          params.set("order", "date");
        }

        const endpoint = PLAYLIST_ID
          ? "https://www.googleapis.com/youtube/v3/playlistItems"
          : "https://www.googleapis.com/youtube/v3/search";

        const res = await fetch(`${endpoint}?${params}`);
        if (!res.ok) throw new Error(`YouTube API error: ${res.status}`);

        const data = await res.json();

        const videos: VideoItem[] = (data.items || []).map(
          (item: { snippet: { resourceId?: { videoId: string }; title: string; publishedAt: string; thumbnails: { high?: { url: string }; medium?: { url: string }; default?: { url: string } }; description: string }; id?: { videoId?: string } }) => {
            const snippet = item.snippet;
            const videoId =
              snippet.resourceId?.videoId || item.id?.videoId || "";
            return {
              id: videoId,
              title: snippet.title,
              published: snippet.publishedAt,
              thumbnail:
                snippet.thumbnails?.high?.url ||
                snippet.thumbnails?.medium?.url ||
                snippet.thumbnails?.default?.url ||
                `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
              description: snippet.description || "",
            };
          },
        );

        if (mounted) setItems(videos);
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchVideos();
    return () => {
      mounted = false;
    };
  }, []);

  return { items, loading, error };
}

function VideoSkeleton() {
  return (
    <div className="animate-pulse overflow-hidden rounded-xl border bg-white p-3">
      <div className="aspect-video rounded bg-gray-200" />
      <div className="mt-3 h-4 w-3/4 rounded bg-gray-200" />
      <div className="mt-2 h-3 w-1/2 rounded bg-gray-200" />
    </div>
  );
}

function VideosGrid() {
  const { items, loading, error } = useYouTubeVideos();
  const [page, setPage] = useState(1);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <VideoSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600">{error}</p>;
  }

  const visible = items.slice(0, page * PER_PAGE);

  return (
    <>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {visible.map((v) => (
          <article
            key={v.id}
            className="overflow-hidden rounded-xl border bg-white transition hover:-translate-y-1 hover:shadow-lg"
          >
            <a
              href={`https://www.youtube.com/watch?v=${v.id}`}
              target="_blank"
              rel="noreferrer"
              className="block"
            >
              <div className="relative">
                <img
                  src={v.thumbnail}
                  alt={v.title}
                  width={720}
                  height={405}
                  className="aspect-video w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-black/45 p-4">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-8 w-8 text-white"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="space-y-2 p-4">
                <h3 className="text-lg font-semibold leading-snug">
                  {v.title}
                </h3>
                <p className="text-xs text-gray-500">
                  {v.published
                    ? new Date(v.published).toLocaleDateString()
                    : ""}
                </p>
                {v.description && (
                  <p className="line-clamp-3 text-sm text-gray-600">
                    {v.description}
                  </p>
                )}
              </div>
            </a>
          </article>
        ))}
      </div>
      {visible.length < items.length && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={() => setPage((p) => p + 1)}>
            Load more videos
          </Button>
        </div>
      )}
    </>
  );
}

function VideosPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>Videos | Larasoft</title>
        <meta
          name="description"
          content="Watch Larasoft product demos, tutorials, and customer walkthroughs from our YouTube channel."
        />
        <link rel="canonical" href="https://larasoft.global/videos" />
        <meta property="og:title" content="Larasoft Videos" />
        <meta
          property="og:description"
          content="Watch tutorials, demos, and client walkthroughs."
        />
        <meta property="og:url" content="https://larasoft.global/videos" />
      </Helmet>
      <Section className="py-16">
        <div className="mx-auto max-w-5xl space-y-8">
          <div className="space-y-4">
            <Badge variant="info">Video library</Badge>
            <h1 className="text-3xl font-semibold text-brand-blue-dark">
              Videos
            </h1>
            <p className="max-w-2xl text-slate-600">
              Explore product demos, tutorials, and customer walkthroughs pulled
              straight from our YouTube playlist.
            </p>
            <div>
              <Button asChild>
                <a href={CHANNEL_URL} target="_blank" rel="noreferrer">
                  Visit the full YouTube channel
                </a>
              </Button>
            </div>
          </div>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <CardTitle>A few of our videos</CardTitle>
              <CardDescription>
                Here are a few of our videos from our YouTube channel. For the
                full library, click the button above to visit our channel.
              </CardDescription>
              <VideosGrid />
            </CardContent>
          </Card>
        </div>
      </Section>
    </MarketingLayout>
  );
}
